import React, { useEffect, useState } from "react";

import { t } from "@lingui/macro";
import { Alert, Checkbox, DatePicker, Form, FormInstance, Input, Spin } from "antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash";

import SelectDrivers from "components/select-drivers";
import SelectFuelStationChains from "components/select-fuel-station-chains";
import SelectVehicles from "components/select-vehicles";

import { useApplicationContext } from "contexts/app";

import { FuelCard } from "types";

import { DEFAULT_DATE_FORMAT } from "utils/datetime-utils";
import { onClearFormErrors } from "utils/form-utils";
import { isUUID } from "utils/uuid-utils";

interface FuelCardsFormsProps {
  form: FormInstance;
  hasPermission?: boolean;
  fuelCardId?: string;
  onFinish: () => void;
  data: FuelCard;
  onFieldChanged: (v: any) => void;
}

const FuelCardsForms = ({ form, fuelCardId, hasPermission, onFinish, data, onFieldChanged }: FuelCardsFormsProps) => {
  const { httpClient, configuration } = useApplicationContext();
  const [loading, setLoading] = useState(false);

  const isUpdate = isUUID(fuelCardId);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        expiration_date: data.expiration_date ? dayjs(data.expiration_date) : data.expiration_date,
      });
    }
  }, [data]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    const body = {
      ...values,
      ...(values?.expiration_date && { expiration_date: dayjs(values.expiration_date).format("YYYY-MM-DD") }),
      account: configuration?.account?.url,
      vehicle: !isEmpty(values.vehicle) ? values.vehicle : null,
      driver: !isEmpty(values.driver) ? values.driver : null,
    };

    try {
      const res = isUpdate
        ? await httpClient.put(`/fuel_cards/${fuelCardId}/`, { data: body, successMsg: t`Fuel card saved`, form })
        : await httpClient.post("/fuel_cards/", { data: body, successMsg: t`Fuel card added`, form });

      if (res) {
        setLoading(false);
        onFinish();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        layout="vertical"
        onFinish={onSubmit}
        disabled={!hasPermission}
        form={form}
        initialValues={{ active: true }}
        onValuesChange={(_, values) => {
          onClearFormErrors(form)(values);
          onFieldChanged(values);
        }}
      >
        {!hasPermission ? (
          <Alert
            type={"warning"}
            message={t`You do not have the permissions to add or edit this accounts fuel cards`}
          />
        ) : null}

        <Form.Item name="number" label={t`Card Number`} required={true}>
          <Input disabled={!isEmpty(data?.number)} />
        </Form.Item>
        <Form.Item name="fuel_station_chain" label={t`Chain`} required={true}>
          <SelectFuelStationChains
            showSearch
            placeholder={t`Choose chain`}
            filterOption={false}
            chain={data?.fuel_station_chain}
            allowClear
            disabled={data?.fuel_station_chain}
          />
        </Form.Item>
        <Form.Item name="vehicle" label={t`Vehicle`}>
          <SelectVehicles
            showSearch
            placeholder={t`Choose a vehicle`}
            filterOption={false}
            vehicle={data?.vehicle}
            allowClear
          />
        </Form.Item>
        <Form.Item name="driver" label={t`Driver`}>
          <SelectDrivers
            showSearch
            placeholder={t`Choose a driver`}
            filterOption={false}
            driver={data?.driver}
            allowClear
          />
        </Form.Item>
        <Form.Item name="pin" label={t`PIN`}>
          <Input />
        </Form.Item>

        <Form.Item name="expiration_date" label={t`Expiration Date`}>
          <DatePicker format={DEFAULT_DATE_FORMAT} style={{ width: "100%" }} disabled={false} showTime={false} />
        </Form.Item>

        <Form.Item name="active" valuePropName="checked">
          <Checkbox disabled={false}> {t`Active`}</Checkbox>
        </Form.Item>
      </Form>
    </Spin>
  );
};

export default FuelCardsForms;
